"use client";
import { useState, useEffect, useCallback } from "react";
import Pagination from "../../Common/Pagination";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../utils/axiosInstance";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { API_ENDPOINTS } from "../../../constants/config";
import { ArrowUpDown, ChevronUp, ChevronDown, Clock } from "lucide-react";

const defaultShiftForm = {
  shiftName: "",
  inTime: "09:00",
  allowedLateCome: 0,
  outTime: "18:00",
  allowedEarlyExit: 0,
  minHalfDay: 4,
  minFullDay: 8,
  considerAsDefault: false,
};

function ShiftForm({ shiftData, onClose, onSuccess }) {
  const isEdit = !!shiftData?._id;
  const [form, setForm] = useState(shiftData ? {
    shiftName: shiftData.shiftName || "",
    inTime: shiftData.inTime || "09:00",
    allowedLateCome: shiftData.allowedLateCome ?? 0,
    outTime: shiftData.outTime || "18:00",
    allowedEarlyExit: shiftData.allowedEarlyExit ?? 0,
    minHalfDay: shiftData.minHalfDay ?? 4,
    minFullDay: shiftData.minFullDay ?? 8,
    considerAsDefault: !!shiftData.considerAsDefault,
  } : { ...defaultShiftForm });
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : type === "number" ? (value === "" ? 0 : Number(value)) : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.shiftName?.trim()) {
      setError("Shift name is required");
      return;
    }
    try {
      const payload = {
        shiftName: form.shiftName.trim(),
        inTime: form.inTime,
        allowedLateCome: Number(form.allowedLateCome) || 0,
        outTime: form.outTime,
        allowedEarlyExit: Number(form.allowedEarlyExit) || 0,
        minHalfDay: Number(form.minHalfDay) || 4,
        minFullDay: Number(form.minFullDay) || 8,
        considerAsDefault: !!form.considerAsDefault,
      };
      if (isEdit) {
        await axiosInstance.patch(API_ENDPOINTS.workShift(shiftData._id), payload);
      } else {
        await axiosInstance.post(API_ENDPOINTS.workShifts, payload);
      }
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || (isEdit ? "Failed to update shift" : "Failed to add shift"));
    }
  };

  return (
    <Card>
      <CardContent className="p-4 sm:p-6">
        <h2 className="mb-4 text-lg font-semibold sm:text-xl">
          {isEdit ? "Edit Work Shift" : "Add Work Shift"}
        </h2>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <p className="mb-3 text-xs font-semibold text-gray-700 sm:text-sm">
              Shift information
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label>Shift Name *</Label>
                <Input name="shiftName" value={form.shiftName} onChange={handleChange} placeholder="e.g. Day Shift" required />
              </div>
            </div>
          </div>

          <div>
            <p className="mb-3 text-xs font-semibold text-gray-700 sm:text-sm">
              Shift time
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label>In Time *</Label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input name="inTime" type="time" value={form.inTime} onChange={handleChange} className="pl-9" required />
                </div>
              </div>
              <div>
                <Label>Allowed Late Come (Min)</Label>
                <Input name="allowedLateCome" type="number" min={0} max={180} value={form.allowedLateCome} onChange={handleChange} onFocus={(e) => e.target.select()} />
              </div>
              <div>
                <Label>Out Time *</Label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input name="outTime" type="time" value={form.outTime} onChange={handleChange} className="pl-9" required />
                </div>
              </div>
              <div>
                <Label>Allowed Early Exit (Min)</Label>
                <Input name="allowedEarlyExit" type="number" min={0} value={form.allowedEarlyExit} onChange={handleChange} onFocus={(e) => e.target.select()} />
              </div>
              <div className="flex items-center gap-2 sm:col-span-2">
                <input
                  type="checkbox"
                  id="considerAsDefault"
                  name="considerAsDefault"
                  checked={form.considerAsDefault}
                  onChange={handleChange}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="considerAsDefault">Use this as the default shift for teachers and staff</Label>
              </div>
            </div>
          </div>

          <div>
            <p className="mb-3 text-xs font-semibold text-gray-700 sm:text-sm">
              Work hours
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label>Min. Half Day (Hrs)</Label>
                <Input name="minHalfDay" type="number" min={0} step={0.5} value={form.minHalfDay} onChange={handleChange} onFocus={(e) => e.target.select()} />
              </div>
              <div>
                <Label>Min. Full Day (Hrs)</Label>
                <Input name="minFullDay" type="number" min={0} step={0.5} value={form.minFullDay} onChange={handleChange} onFocus={(e) => e.target.select()} />
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button type="submit" className="w-full sm:w-auto">
              {isEdit ? "Update Shift" : "Add Shift"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

export function WorkShiftList() {
  const navigate = useNavigate();
  const [shifts, setShifts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const getSortIcon = (field) => {
    if (field !== sortField) return <ArrowUpDown className="h-4 w-4 text-gray-400 ml-1" />;
    return sortDirection === "asc" ? <ChevronUp className="h-4 w-4 text-[#0263cc] ml-1" /> : <ChevronDown className="h-4 w-4 text-[#0263cc] ml-1" />;
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSorted = () => {
    if (!sortField) return shifts;
    return [...shifts].sort((a, b) => {
      let va = a[sortField];
      let vb = b[sortField];
      if (sortField === "createdAt") {
        va = new Date(va).getTime();
        vb = new Date(vb).getTime();
      }
      if (typeof va === "string") va = va.toLowerCase();
      if (typeof vb === "string") vb = vb.toLowerCase();
      if (va < vb) return sortDirection === "asc" ? -1 : 1;
      if (va > vb) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  };

  const fetchShifts = useCallback(async () => {
    try {
      setError(null);
      const res = await axiosInstance.get(API_ENDPOINTS.workShifts);
      setShifts(res.data?.data?.shifts || []);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch work shifts");
    }
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this work shift?")) return;
    try {
      setError(null);
      await axiosInstance.delete(API_ENDPOINTS.workShift(id));
      fetchShifts();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete shift");
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }, []);

  useEffect(() => {
    fetchShifts();
  }, [fetchShifts]);

  const onFormSuccess = () => {
    setShowForm(false);
    setSelectedShift(null);
    fetchShifts();
  };

  return (
    <Card className="w-full">
      <CardContent className="p-4 sm:p-6">
        <div className="mb-4 flex flex-col items-stretch justify-between gap-3 sm:mb-6 sm:flex-row sm:items-center">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              onClick={fetchShifts}
              className="w-full sm:w-auto"
            >
              Refresh List
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/dashboard/settings/shift-mapping")}
              className="w-full sm:w-auto"
            >
              Teacher/Staff Mapping
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/dashboard/settings/shift-planning")}
              className="w-full sm:w-auto"
            >
              Shift Planning
            </Button>
          </div>
          <Button
            onClick={() => {
              setSelectedShift(null);
              setShowForm(true);
            }}
            className="w-full sm:w-auto"
          >
            Add Work Shift
          </Button>
        </div>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {/* Desktop table */}
        <div className="hidden md:block">
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead
                    className="cursor-pointer font-semibold text-gray-600"
                    onClick={() => handleSort("shiftName")}
                  >
                    <div className="flex items-center">
                      Shift Name {getSortIcon("shiftName")}
                    </div>
                  </TableHead>
                  <TableHead>In / Out</TableHead>
                  <TableHead>Min Hrs (Half/Full)</TableHead>
                  <TableHead>Default</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {getSorted().slice((page - 1) * itemsPerPage, page * itemsPerPage).map((s) => (
                  <TableRow key={s._id}>
                    <TableCell>{s.shiftName}</TableCell>
                    <TableCell>
                      {s.inTime} – {s.outTime}
                    </TableCell>
                    <TableCell>
                      {s.minHalfDay} / {s.minFullDay}
                    </TableCell>
                    <TableCell>{s.considerAsDefault ? "Yes" : "No"}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setSelectedShift(s);
                            setShowForm(true);
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="destructive"
                          onClick={() => handleDelete(s._id)}
                        >
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Mobile / small tablet list */}
        <div className="space-y-3 md:hidden">
          {getSorted().length === 0 && (
            <div className="rounded-md border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
              No work shifts found. Use &quot;Add Work Shift&quot; to create one.
            </div>
          )}
          {getSorted().slice((page - 1) * itemsPerPage, page * itemsPerPage).map((s) => (
            <div
              key={s._id}
              className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-gray-900">
                    {s.shiftName}
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    In / Out:{" "}
                    <span className="font-medium">
                      {s.inTime} – {s.outTime}
                    </span>
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    Min Hrs (Half / Full):{" "}
                    <span className="font-medium">
                      {s.minHalfDay} / {s.minFullDay}
                    </span>
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    Default:{" "}
                    <span className="font-medium">
                      {s.considerAsDefault ? "Yes" : "No"}
                    </span>
                  </p>
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => {
                    setSelectedShift(s);
                    setShowForm(true);
                  }}
                >
                  Edit
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  className="w-full"
                  onClick={() => handleDelete(s._id)}
                >
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
        <Pagination
          currentPage={page}
          totalItems={shifts.length}
          itemsPerPage={itemsPerPage}
          onPageChange={setPage}
          onItemsPerPageChange={(newLimit) => { setItemsPerPage(newLimit); setPage(1); }}
          itemLabel="shifts"
        />

        {showForm && (
          <div className="mt-6">
            <ShiftForm
              shiftData={selectedShift}
              onClose={() => { setShowForm(false); setSelectedShift(null); }}
              onSuccess={onFormSuccess}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
